// eslint-disable-next-line @typescript-eslint/no-unused-vars
import React, { useEffect, useState, useMemo, useCallback, FC } from "react";
// import { useSelector, useDispatch } from "react-redux";
import { BrowserRouter } from "react-router-dom";
import { ConfigProvider, theme } from "antd";
import { StyleProvider } from "@ant-design/cssinjs";

// Fix all pdf-viewers errors:
// https://github.com/wojtekmaj/react-pdf#support-for-annotations
import { pdfjs } from "react-pdf";
import "@cyntler/react-doc-viewer/dist/index.css";

// Routes:
import ClientRoutes from "./libs/routes.tsx";
// Redux-hooks:
import { useAppSelector, useAppDispatch } from "./libs/redux-hooks.ts";
// Reducers:
import {
  reloadConfigAndApps, setDesktopType, getLocale, getTheme, getCompact, pushSocketAction, clearSocketAction, clearSocketActionsReducer
} from "./libs/reducer.ts";

// Socket
import socket from "./libs/socket.ts";

// Global typescript declatarions for js-files from "window" object:
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import * as globalDeclarations from "./types/globalDeclarations.ts";
// import fnc from "./libs/fnc.ts";
import "./libs/global.tsx";

// @ts-ignore
// pdfjs.GlobalWorkerOptions.workerSrc = new URL("pdfjs-dist/build/pdf.worker.min.mjs", import.meta.url).toString();
pdfjs.GlobalWorkerOptions.workerSrc = "/js/libs/pdfjs/pdf.worker.min.mjs";

const { rz, $ } = window;

let appTimerRun = false, lastDesktopType = false, appTimerID:number; // lastDesktopHeight = 0
let stopDoubleInit = false;

const crchrk = async () => {
  if (!crypto || !crypto.subtle || !crypto.subtle.digest) { return "" }
  const str = "o0091-" + (rz.dateToString(new Date(), "YYYY-MM-DD")).replace(/\d{2,2}$/, "") + "sR-q2192168909v.uen12scywla72_z";
  const txtEncoder = new TextEncoder(), encStr = txtEncoder.encode(str);
  const hashBuffer = await crypto.subtle.digest("SHA-256", encStr);
  const hashArray = new Uint8Array(hashBuffer);
  // @ts-ignore
  const keyBase64 = btoa(String.fromCharCode(...hashArray));
  return keyBase64;
};

/* React-app */

const RzApp:FC = () => {
  const dispatch = useAppDispatch();
  const locale = useAppSelector(getLocale);
  const appTheme = useAppSelector(getTheme);
  const appCompact = useAppSelector(getCompact);
  const [loading, setLoading] = useState(true);

  const updateDesktopType = useCallback(() => {
    const isMDevice = rz.isMobileDevice(navigator.userAgent);
    if (isMDevice !== lastDesktopType) {
      dispatch(setDesktopType(isMDevice)); lastDesktopType = isMDevice;
      rz.log({message: "DeviceType was changed to " + (isMDevice ? "Mobile" : "Computer") + " type", module: "app.tsx", caller: "component <RzApp> -> timerRun() -> updateDesktopType()"});
    }
  }, [dispatch]);

  // Application Timer
  const timerRun = useCallback(() => {
    appTimerRun = true;
    appTimerID = window.setInterval(() => {
      updateDesktopType(); // Check Device Type: (Desktop/Tablet)
    }, 3000);
  }, [updateDesktopType]);

  useEffect(() => {
    timerRun(); // Application Timer (Every 3 second)
  }, [timerRun]);

  // =========== Component did mount ===========
  useEffect(() => {
    if (!stopDoubleInit) {
      stopDoubleInit = true;
      // Socket.io
      crchrk().then(async (key) => {
        if (!key) return;
        const data1 = "Hello from Socket.io-client!!!", data2 = await rz.encryptData(rz.errToString(data1), key);
        socket.emit("event", {event: "testSocketConnection", data: data2});
        // Auto-update AGgrid rows when data on the server was updated too
        socket.onAny(async (event, encrypted) => {
          if (!event || !encrypted || !encrypted.data || !encrypted.iv) return;
          const ans1 = await rz.decryptData(encrypted, key); if (!ans1 || !ans1.data) return;
          let {data} = ans1;
          if (rz.isString(data)) {
            if (data.startsWith("\"") || data.endsWith("\"")) { data = data.slice(1, -1) }
            if (data.startsWith("{") || data.startsWith("[")) {
              data = rz.objFromJSON(rz.strReplaceAll(data, ["\\\\", "\\\""], ["\\", "\""]));
            }
          }

          if (window.location.protocol === "http:" && event !== "systemlogs.event") console.log("onSocketAction()", {event, data, clientid: socket.id});
          if (data) {
            const keyTime = (new Date()).getTime();
            dispatch(pushSocketAction({event, data, key: keyTime}));
            setTimeout((kk) => {
              dispatch(clearSocketAction(kk));
            }, 2000, keyTime); // After 2 seconds App will clear this socket-action with data
          }
        });
      });

      // Hide preloader. Show page content
      $("#preloader").fadeOut("slow"); $(".content").show(); $("body").removeClass("stop-scrolling");
      // Show rz-main-container
      $(".rz-main-container").show().removeAttr("hidden");

      dispatch(reloadConfigAndApps()).then(() => { setLoading(false) });

      // ========= Component will unmount =========
      return (() => {
        if (appTimerRun && appTimerID) { window.clearInterval(appTimerID) }
        socket.emit("unsubscribe", {room: "any"});
        dispatch(clearSocketActionsReducer());
        // if (fnc.config?.isDev)
        // rz.log({message: "Component <RzApp> was unmount -> socket.emit('unsubscribe')", module: "app.tsx", caller: "component <RzApp> -> useEffect()[] -> return"});
      });
    }
  }, [dispatch]);

  const themeConfig = useMemo(() => {
    const thm:{algorithm:Array<any>} = {algorithm: []};
    if (appCompact) { thm.algorithm.push(theme.compactAlgorithm) }
    if (appTheme === "dark") { thm.algorithm.push(theme.darkAlgorithm) }
    return thm;
  }, [appCompact, appTheme]);

  return (
    (loading
      ? <div className="rz-reactapp" />
      : <ConfigProvider locale={locale} theme={themeConfig}>
          <StyleProvider>
            <BrowserRouter>
              <ClientRoutes />
            </BrowserRouter>
          </StyleProvider>
        </ConfigProvider>
    )
  );
}

export default RzApp;
